"use client";

import { ArrowUpRight } from "lucide-react";
import { getCorpusAssets } from "../../assets";
import { SparkleAvatar, TickerBadge } from "./shared";

// // // //

/**
 * AssetPicker
 *
 * The dock's "new chat" launcher (design story `new`). Chats are grounded per
 * asset, so this adapts the mock's cross-asset welcome into an asset chooser: a
 * gradient intro over a scrollable list of corpus assets, each a ticker chip +
 * name + one-liner that opens a fresh grounded conversation.
 */
export function AssetPicker(props: {
    onPick: (asset: { id: string; name: string }) => void;
}) {
    const { onPick } = props;
    const assets = getCorpusAssets();

    return (
        <div className="h-full overflow-y-auto px-4 pt-6 pb-4">
            <div className="text-center">
                <SparkleAvatar className="mx-auto size-11" />
                <h3 className="mt-4 text-[17px] font-semibold text-foreground">
                    Ask anything about a fund
                </h3>
                <p className="mx-auto mt-1.5 max-w-[18rem] text-[13px] leading-snug text-tertiary-foreground">
                    Fees, liquidity, minimums, risks — answered straight from
                    the fund&apos;s data room documents.
                </p>
            </div>

            <ul className="mt-5 space-y-2">
                {assets.map((asset) => (
                    <li key={asset.id}>
                        <button
                            type="button"
                            onClick={() =>
                                onPick({ id: asset.id, name: asset.name })
                            }
                            className="group/pick flex w-full items-center gap-3 rounded-xl border border-muted bg-card px-3 py-2.5 text-left transition hover:border-primary-light hover:bg-primary-light/30 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                        >
                            <TickerBadge ticker={asset.id} active />
                            <div className="min-w-0 flex-1">
                                <div className="truncate text-sm font-medium text-foreground">
                                    {asset.name}
                                </div>
                                <div className="truncate text-xs text-tertiary-foreground">
                                    {asset.shortDescription}
                                </div>
                            </div>
                            <ArrowUpRight
                                className="size-4 shrink-0 text-muted-foreground transition group-hover/pick:translate-x-0.5 group-hover/pick:text-primary"
                                aria-hidden="true"
                            />
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}
